import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, Image, ScrollView, TouchableOpacity, ActivityIndicator, StyleSheet, Platform, Linking, Modal } from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { getFirestore, doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { globalStyles } from '../styles/globalStyles';
import { profileStyles } from '../styles/ProfileStyles';
import { venueStyles } from '../styles/venueStyles';
import { VerifiedBadge } from '../components/VerifiedBadge';
import { BackgroundVideo } from '../components/BackgroundVideo';
import { CommunityPostCard } from '../components/CommunityPostCard';
import { BREAKPOINTS, getResponsiveValue } from '../styles/breakpoints';
import { useCommunityModal } from '../contexts/CommunityModalContext';
import { useUser } from '../components/LoginPersistence';
import { Ionicons } from '@expo/vector-icons';
import ProfileTabbedMediaManager from '../components/ProfileTabbedMediaManager';
import { ProfileTemplateCard } from '../components/ProfileTemplateCard';
import { useInterestCatalog } from '../hooks/useInterestCatalog';
import { resolveInterestDisplayItems } from '../utils/interestUtils';

const getFileType = (fileName) => {
  const extension = fileName.split('.').pop().toLowerCase();

  // Image types
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
    return 'image';
  }

  // PDF
  if (extension === 'pdf') {
    return 'pdf';
  }

  // Audio types
  if (['mp3', 'wav', 'aac', 'flac', 'ogg', 'm4a', 'wma'].includes(extension)) {
    return 'audio';
  }

  // Video types
  if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
    return 'video';
  }

  // Default to file
  return 'file';
};

const USER_TYPES = {
  ADMIN: {
    label: 'Admin',
    value: 'admin',
    color: 'pink'
  },
  MEMBER: {
    label: 'Member',
    value: 'member',
    color: '#0bb5df'
  },
  VENUE_EMPLOYEE: {
    label: 'Venue Employee',
    value: 'venue_employee',
    color: '#2ecc71'
  },
  VENUE_OWNER: {
    label: 'Venue Owner',
    value: 'venue_owner',
    color: '#9b59b6'
  },
  ARTIST: {
    label: 'Artist',
    value: 'artist',
    color: '#e67e22'
  },
  BAND_MEMBER: {
    label: 'Band Member',
    value: 'band_member',
    color: '#f1c40f'
  },
  MUSIC_INDUSTRY: {
    label: 'Music Industry Employee',
    value: 'music_industry',
    color: '#1abc9c'
  }
};

const UserProfileScreen = () => {
  const route = useRoute();
  const navigation = useNavigation();
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const db = getFirestore();
  const { openMessaging } = useCommunityModal();
  const { user: currentUserData, setUser } = useUser();
  
  console.log('UserProfileScreen: Context values:', {
    openMessaging: typeof openMessaging,
    openMessagingFunction: openMessaging
  });
  
  const [userData, setUserData] = useState(null);
  const [userPosts, setUserPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [loading, setLoading] = useState(true);
  const [existingConversation, setExistingConversation] = useState(null);
  const [checkingConversation, setCheckingConversation] = useState(false);
  const [showFolderSelectModal, setShowFolderSelectModal] = useState(false);
  const [postToSave, setPostToSave] = useState(null);

  const { userId } = route.params;

  const hasMigratedProfileCard = Boolean(
    userData &&
      userData.profileCardTemplate &&
      Array.isArray(userData.profileCardTemplate.items) &&
      userData.profileCardTemplate.items.length > 0
  );

  const showPublicProfileCard =
    hasMigratedProfileCard && !!userData?.profileCardTemplatePublic;

  const { interests: interestCatalog } = useInterestCatalog();
  const profileInterestItems = useMemo(
    () => resolveInterestDisplayItems(userData?.interests || [], interestCatalog),
    [userData?.interests, interestCatalog]
  );

  useEffect(() => {
    loadUserData();
    checkExistingConversation();
  }, [userId]);

  const loadUserData = async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        setUserData({ id: userDoc.id, ...userDoc.data() });
        await loadUserPosts(userDoc.id);
      } else {
        console.error('User not found');
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserPosts = async (targetUserId) => {
    if (!targetUserId) {
      setUserPosts([]);
      setLoadingPosts(false);
      return;
    }
    try {
      setLoadingPosts(true);
      const postsQuery = query(
        collection(db, 'communityPosts'),
        where('userId', '==', targetUserId)
      );
      const postsSnapshot = await getDocs(postsQuery);
      const posts = postsSnapshot.docs.map((postDoc) => ({
        id: postDoc.id,
        ...postDoc.data(),
      }));
      posts.sort((a, b) => {
        const aTs = a?.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const bTs = b?.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return bTs - aTs;
      });
      setUserPosts(posts);
    } catch (error) {
      console.error('Error loading user posts:', error);
      setUserPosts([]);
    } finally {
      setLoadingPosts(false);
    }
  };

  const checkExistingConversation = async () => {
    if (!currentUser?.uid) return;
    
    try {
      setCheckingConversation(true);
      
      // Check if direct conversation already exists
      const conversationsQuery = query(
        collection(db, 'conversations'),
        where('participants', 'array-contains', currentUser.uid)
      );

      const snapshot = await getDocs(conversationsQuery);
      
      for (const docSnap of snapshot.docs) {
        const conversationData = docSnap.data();
        if (
          conversationData.type === 'direct' &&
          conversationData.participants.includes(userId) &&
          conversationData.participants.length === 2
        ) {
          setExistingConversation({ id: docSnap.id, ...conversationData });
          break;
        }
      }
    } catch (error) {
      console.error('Error checking existing conversation:', error);
    } finally {
      setCheckingConversation(false);
    }
  };

  const handleMessageUser = () => {
    if (!currentUser?.uid) {
      alert('Please log in to send messages');
      return;
    }

    if (currentUser.uid === userId) {
      alert('You cannot message yourself');
      return;
    }

    // Check if current user is a community member
    if (!currentUserData?.isCommunityMember) {
      alert('You must be a community member to send messages');
      return;
    }

    console.log('UserProfileScreen: handleMessageUser called');
    console.log('UserProfileScreen: userId =', userId);
    console.log('UserProfileScreen: existingConversation?.id =', existingConversation?.id);
    console.log('UserProfileScreen: openMessaging function =', openMessaging);
    console.log('UserProfileScreen: openMessaging type =', typeof openMessaging);
    console.log('UserProfileScreen: openMessaging.toString() =', openMessaging.toString());
    
    try {
      // Open the community modal and navigate to messages tab with target user
      openMessaging(userId, existingConversation?.id);
      console.log('UserProfileScreen: openMessaging called successfully');
    } catch (error) {
      console.error('UserProfileScreen: Error calling openMessaging:', error);
    }
  };

  if (loading) {
    return (
      <View style={globalStyles.container}>
        <BackgroundVideo />
        <View style={globalStyles.overlay}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#4e7de0" />
            <Text style={styles.loadingText}>Loading profile...</Text>
          </View>
        </View>
      </View>
    );
  }

  if (!userData) {
    return (
      <View style={globalStyles.container}>
        <BackgroundVideo />
        <View style={globalStyles.overlay}>
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>User not found</Text>
            <TouchableOpacity
              style={styles.backButton}
              onPress={() => navigation.goBack()}
            >
              <Text style={styles.backButtonText}>Go Back</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={[globalStyles.container, { width: '100%' }]}>
      <BackgroundVideo />
      <View style={globalStyles.overlay}>
        <ScrollView   style={styles.scrollView}>
          {/* Content Container matching Profile.js */}
          <View style={styles.contentContainer}>
            {/* Back Button matching VenueScreen.js */}
            <TouchableOpacity 
              style={venueStyles.backButton}
              onPress={() => navigation.goBack()}
            >
              <Text style={venueStyles.backButtonText}>← Back</Text>
            </TouchableOpacity>

            {/* Profile: grid template when public; legacy yellow card otherwise */}
            <View style={styles.profileContainer}>
              {showPublicProfileCard ? (
                <View style={{ width: '100%', alignItems: 'stretch' }}>
                  <ProfileTemplateCard
                    template={userData.profileCardTemplate}
                    onPress={() => {}}
                    onMessagePress={() => handleMessageUser()}
                    showMessageButton={
                      !!currentUser?.uid &&
                      currentUser.uid !== userId &&
                      !!currentUserData?.isCommunityMember &&
                      !!userData?.isCommunityMember
                    }
                    interestItems={profileInterestItems}
                    showInterestEmojis={
                      userData.profileCardTemplate?.showInterestsOnCard !== false &&
                      profileInterestItems.length > 0
                    }
                    activeOpacity={1}
                  />
                  <View style={[styles.statsContainer, { marginTop: 16 }]}>
                    <Text style={styles.statsText}>
                      Joined{' '}
                      {userData.createdAt
                        ? new Date(userData.createdAt.toDate()).toLocaleDateString()
                        : 'Recently'}
                    </Text>
                    {userData.lastSeen && (
                      <Text style={styles.statsText}>
                        Last seen {new Date(userData.lastSeen.toDate()).toLocaleDateString()}
                      </Text>
                    )}
                  </View>
                </View>
              ) : !hasMigratedProfileCard ? (
              <View style={[styles.profileCard, {
                backgroundColor: '#FFE99C',
                borderWidth: 3,
                borderColor: userData.isPrivate === false ? '#4caf50' : userData.isPrivate === true ? '#ff3b30' : '#666769',
                borderStyle: 'solid'
              }]}>
              
              {/* Profile Photo */}
              <View style={styles.profilePhotoContainer}>
                {userData.photoURL ? (
                  <Image source={{ uri: userData.photoURL }} style={styles.profilePhoto} />
                ) : (
                  <View style={styles.profilePhotoPlaceholder}>
                    <Text style={styles.profilePhotoPlaceholderText}>
                      {userData.firstName ? userData.firstName[0].toUpperCase() : '?'}
                    </Text>
                  </View>
                )}
              </View>

              {/* Verification Badge */}
              {userData.verificationStatus === 'approved' && (
                <View style={styles.verificationBadgeContainer}>
                  <VerifiedBadge />
                </View>
              )}

              {/* Username */}
              <Text style={[styles.profileName, { color: '#000000' }]}>
                {userData.username || 'Anonymous User'}
              </Text>

              {/* User Type */}
              <Text style={[styles.userTypeText, {
                color: USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df'
              }]}>
                {USER_TYPES[userData?.userType?.toUpperCase()]?.label || 'Member'}
              </Text>

              {/* Verification Status */}
              {userData.verificationStatus && (
                <Text style={[
                  styles.verificationStatus,
                  userData.verificationStatus === 'approved' && [
                    styles.verificationApproved,
                    { color: USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df' }
                  ],
                  userData.verificationStatus === 'pending' && styles.verificationPending
                ]}>
                  {userData.verificationStatus === 'approved' ? 'Verified User' : 'Verification Pending'}
                </Text>
              )}

              {/* Location */}
              {userData.location && (
                <View style={styles.locationContainer}>
                  <Text style={styles.locationLabel}>Location:</Text>
                  <Text style={styles.locationText}>{userData.location}</Text>
                </View>
              )}

              {/* Bio */}
              {userData.bio && (
                <View style={styles.bioContainer}>
                  <Text style={styles.bioLabel}>Bio:</Text>
                  <Text style={styles.bioText}>{userData.bio}</Text>
                </View>
              )}

              {/* Public Media Display */}
              {userData.publicMedia?.media && userData.publicMedia.media.length > 0 && (
                <View style={styles.publicMediaContainer}>
                  <Text style={styles.mediaLabel}>Media:</Text>
                  <View style={styles.mediaPreviewGrid}>
                    {userData.publicMedia.media.slice(0, 3).map((item, index) => (
                      <TouchableOpacity
                        key={item.id || index}
                        style={styles.mediaPreviewItem}
                        onPress={() => Linking.openURL(item.url)}
                      >
                        {getFileType(item.name) === 'image' ? (
                          <Image
                            source={{ uri: item.url }}
                            style={styles.mediaPreviewImage}
                          />
                        ) : getFileType(item.name) === 'pdf' ? (
                          <View style={[styles.mediaPreviewIcon, { backgroundColor: '#e74c3c' }]}>
                            <Ionicons name="document-text" size={24} color="#ffffff" />
                            <Text
                              style={styles.mediaPreviewLabel}
                              numberOfLines={1}
                              ellipsizeMode="middle"
                            >
                              {item.name}
                            </Text>
                          </View>
                        ) : getFileType(item.name) === 'audio' ? (
                          <View style={styles.audioWaveformPreview}>
                            <View style={styles.audioWaveformContainer}>
                              <View style={[styles.audioWaveformBar, { height: 4 }]} />
                              <View style={[styles.audioWaveformBar, { height: 28 }]} />
                              <View style={[styles.audioWaveformBar, { height: 6 }]} />
                              <View style={[styles.audioWaveformBar, { height: 32 }]} />
                              <View style={[styles.audioWaveformBar, { height: 10 }]} />
                            </View>
                            {Platform.OS === 'web' && (
                              <audio src={item.url} controls style={{ width: '100%', marginTop: 6 }} />
                            )}
                          </View>
                        ) : getFileType(item.name) === 'video' ? (
                          <View style={styles.mediaPreviewContainer}>
                            <video
                              src={item.url}
                              style={styles.mediaPreviewImage}
                              muted
                              preload="metadata"
                            />
                            <View style={styles.mediaOverlay}>
                              <Ionicons name="play-circle" size={24} color="#ffffff" />
                            </View>
                            <View style={styles.videoDurationOverlay}>
                              <Text style={styles.durationText}>
                                {item.duration || '0:00'}
                              </Text>
                            </View>
                          </View>
                        ) : (
                          <View style={[styles.mediaPreviewIcon, { backgroundColor: '#34495e' }]}>
                            <Ionicons name="attach" size={16} color="#ffffff" />
                            <Text
                              style={styles.mediaPreviewLabel}
                              numberOfLines={1}
                              ellipsizeMode="middle"
                            >
                              FILE
                            </Text>
                          </View>
                        )}
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
              )}

              {/* Social Links */}
              {userData.socialLinks && userData.socialLinks.length > 0 && (
                <View style={styles.socialLinksContainer}>
                  <Text style={styles.socialLinksLabel}>Social Links:</Text>
                  <View style={styles.socialLinksButtons}>
                    {userData.socialLinks.map((link, index) => {
                      const linkLower = (link || '').toLowerCase();
                      let label = '🔗 Link';
                      
                      if (linkLower.includes('spotify')) label = '🎵 Spotify';
                      else if (linkLower.includes('instagram')) label = '📷 Instagram';
                      else if (linkLower.includes('facebook')) label = '👍 Facebook';
                      else if (linkLower.includes('twitter') || linkLower.includes('x.com')) label = '🐦 Twitter';
                      else if (linkLower.includes('youtube')) label = '▶️ YouTube';
                      else if (linkLower.includes('soundcloud')) label = '🎧 SoundCloud';
                      else if (linkLower.includes('tiktok')) label = '🎬 TikTok';
                      else if (linkLower.includes('apple')) label = '🍎 Apple Music';
                      else if (linkLower.includes('bandcamp')) label = '🎸 Bandcamp';
                      
                      return (
                        <TouchableOpacity 
                          key={index} 
                          style={styles.socialLinkButton}
                          onPress={() => Linking.openURL(link)}
                        >
                          <Text style={styles.socialLinkButtonText}>{label}</Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>
              )}

              {/* Message Button - Only show if current user is a community member */}
              {(currentUser?.uid && currentUser.uid !== userId && currentUserData?.isCommunityMember && userData?.isCommunityMember) && (
                <TouchableOpacity
                  style={styles.messageButton}
                  onPress={handleMessageUser}
                  disabled={checkingConversation}
                >
                  {checkingConversation ? (
                    <ActivityIndicator size="small" color="#ffffff" />
                  ) : (
                    <Text style={styles.messageButtonText}>
                      Message {userData.firstName || 'User'}
                    </Text>
                  )}
                </TouchableOpacity>
              )}

              {/* Profile Stats */}
              <View style={styles.statsContainer}>
                <Text style={styles.statsText}>
                  Joined {userData.createdAt ? new Date(userData.createdAt.toDate()).toLocaleDateString() : 'Recently'}
                </Text>
                {userData.lastSeen && (
                  <Text style={styles.statsText}>
                    Last seen {new Date(userData.lastSeen.toDate()).toLocaleDateString()}
                  </Text>
                )}
              </View>
            </View>
              ) : null}

          </View>
          <View style={styles.postsContainer}>
            <View style={styles.postsSeparator} />
            <Text style={styles.postsSectionTitle}>Post:</Text>
            {loadingPosts ? (
              <View style={styles.postsLoadingContainer}>
                <ActivityIndicator size="small" color="#4e7de0" />
                <Text style={styles.postsLoadingText}>Loading posts...</Text>
              </View>
            ) : userPosts.length > 0 ? (
              <View style={styles.postsList}>
                {userPosts.map((post) => (
                  <View key={post.id} style={styles.postCardWrap}>
                    <CommunityPostCard
                      post={post}
                      onPress={() => {}}
                      onMessagePress={() => handleMessageUser()}
                      onFolderPress={(selectedPost) => {
                        if (!currentUserData?.uid) {
                          alert('Please sign in to save posts to a folder.');
                          return;
                        }
                        setPostToSave(selectedPost);
                        setShowFolderSelectModal(true);
                      }}
                    />
                  </View>
                ))}
              </View>
            ) : (
              <Text style={styles.postsEmptyText}>No posts yet.</Text>
            )}
          </View>

          <Modal
            visible={showFolderSelectModal && !!postToSave}
            transparent
            animationType="fade"
            onRequestClose={() => { setShowFolderSelectModal(false); setPostToSave(null); }}
          >
            <View style={styles.modalOverlay}>
              <View style={styles.folderSelectModalContent}>
                <View style={styles.folderSelectHeader}>
                  <Text style={styles.folderSelectHeaderText}>Select folder to save post</Text>
                  <TouchableOpacity onPress={() => { setShowFolderSelectModal(false); setPostToSave(null); }} style={{ padding: 8 }}>
                    <Ionicons name="close" size={24} color="#1d1d1d" />
                  </TouchableOpacity>
                </View>
                <View style={{ flex: 1 }}>
                  {currentUserData && (
                    <ProfileTabbedMediaManager
                      libraryTab="community"
                      userData={currentUserData}
                      setUser={setUser}
                      onEditReport={() => {}}
                      onDeleteReport={() => {}}
                      onViewVenue={() => {}}
                      onAddVenuePhoto={() => {}}
                      onDeleteVenuePhoto={() => {}}
                      onFolderSelect={() => { setShowFolderSelectModal(false); setPostToSave(null); }}
                      communityPostToSave={postToSave}
                      allowFileUpload={false}
                      allowProfileDropImport={false}
                    />
                  )}
                </View>
              </View>
            </View>
          </Modal>
          </View>
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    width: '100%',
  },
  contentContainer: {
    width: '100%',
    paddingHorizontal: 20,
    marginHorizontal: 'auto',
    paddingTop: Platform.OS === 'web' && typeof window !== 'undefined' && window.innerWidth <= BREAKPOINTS.tablet ? 80 : 60,
    alignItems: 'center',
    paddingBottom: 20,
  },
  profileContainer: {
    width: '100%',
    maxWidth: 500,
    minWidth: 300,
    alignItems: 'center',
    marginTop: 20,
  },
  profileCard: {
    width: '100%',
    padding: 30,
    borderRadius: 20,
    alignItems: 'center',
    position: 'relative',
    minHeight: 400,
  },
  profilePhotoContainer: {
    marginBottom: 20,
    position: 'relative',
  },
  profilePhoto: {
    width: 120,
    height: 120,
    borderRadius: 60,
    borderWidth: 4,
    borderColor: '#000000',
  },
  profilePhotoPlaceholder: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 4,
    borderColor: '#000000',
  },
  profilePhotoPlaceholderText: {
    color: '#000000',
    fontSize: 48,
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
  },
  verificationBadgeContainer: {
    position: 'absolute',
    top: 30,
    right: 30,
  },
  profileName: {
    fontSize: 28,
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
    marginBottom: 8,
    textAlign: 'center',
  },
  userTypeText: {
    fontSize: 18,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    marginBottom: 8,
    textAlign: 'center',
  },
  verificationStatus: {
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    marginBottom: 20,
    textAlign: 'center',
  },
  verificationApproved: {
    fontWeight: '600',
  },
  verificationPending: {
    color: '#f1c40f',
  },
  locationContainer: {
    width: '100%',
    marginBottom: 15,
  },
  locationLabel: {
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    color: '#000000',
    marginBottom: 4,
  },
  locationText: {
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    color: '#000000',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    padding: 12,
    borderRadius: 8,
  },
  bioContainer: {
    width: '100%',
    marginBottom: 20,
  },
  bioLabel: {
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    color: '#000000',
    marginBottom: 4,
  },
  bioText: {
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    color: '#000000',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    padding: 12,
    borderRadius: 8,
    lineHeight: 22,
    minHeight: 60,
  },
  publicMediaContainer: {
    width: '100%',
    marginBottom: 20,
  },
  mediaLabel: {
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    color: '#000000',
    marginBottom: 8,
  },
  mediaPreviewGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'flex-start',
  },
  mediaPreviewItem: {
    width: 90,
    height: 90,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#f0f0f0',
  },
  mediaPreviewImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  mediaPreviewContainer: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  mediaOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
  videoDurationOverlay: {
    position: 'absolute',
    bottom: 4,
    right: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 4,
  },
  durationText: {
    color: '#ffffff',
    fontSize: 10,
    fontFamily: 'VastagoGrotesk',
  },
  mediaPreviewIcon: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 8,
  },
  mediaPreviewLabel: {
    color: '#ffffff',
    fontSize: 10,
    fontFamily: 'VastagoGrotesk',
    textAlign: 'center',
    marginTop: 4,
  },
  audioWaveformPreview: {
    width: '100%',
    height: '100%',
    backgroundColor: '#3498db',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 8,
  },
  audioWaveformContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  },
  audioWaveformBar: {
    width: 4,
    backgroundColor: '#ffffff',
    borderRadius: 2,
  },
  socialLinksContainer: {
    width: '100%',
    marginBottom: 20,
  },
  socialLinksLabel: {
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    color: '#000000',
    marginBottom: 8,
  },
  socialLinksButtons: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -4,
  },
  socialLinkButton: {
    backgroundColor: '#3b82f6',
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 16,
    marginHorizontal: 4,
    marginVertical: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  socialLinkButtonText: {
    color: '#ffffff',
    fontSize: 12,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  messageButton: {
    backgroundColor: '#4e7de0',
    borderRadius: 25,
    paddingVertical: 15,
    paddingHorizontal: 30,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  messageButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  statsContainer: {
    marginTop: 20,
    alignItems: 'center',
  },
  statsText: {
    fontSize: 12,
    fontFamily: 'VastagoGrotesk',
    color: 'rgba(0, 0, 0, 0.6)',
    marginBottom: 4,
  },
  postsContainer: {
    width: '100%',
    marginTop: 24,
    marginBottom: 8,
    paddingHorizontal: getResponsiveValue({ mobile: 12, tablet: 20, laptop: 0, desktop: 0 }),
  },
  postsSeparator: {
    height: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    marginBottom: 12,
  },
  postsSectionTitle: {
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    color: '#000000',
    marginBottom: 10,
  },
  postsLoadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.06)',
    borderRadius: 8,
    padding: 12,
  },
  postsLoadingText: {
    fontSize: 13,
    fontFamily: 'VastagoGrotesk',
    color: '#444',
  },
  postsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 10,
  },
  postCardWrap: {
    width: '100%',
    maxWidth: 450,
  },
  postsEmptyText: {
    fontSize: 13,
    fontFamily: 'VastagoGrotesk',
    color: '#666',
    fontStyle: 'italic',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  loadingText: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    marginTop: 15,
    textAlign: 'center',
  },
  errorContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  errorText: {
    color: '#ffffff',
    fontSize: 18,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    marginBottom: 20,
    textAlign: 'center',
  },
  backButton: {
    backgroundColor: '#4e7de0',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 20,
  },
  backButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  folderSelectModalContent: {
    width: '100%',
    maxWidth: 1100,
    height: '90%',
    backgroundColor: '#ffffff',
    borderRadius: 12,
    overflow: 'hidden',
  },
  folderSelectHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.08)',
  },
  folderSelectHeaderText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 18,
    fontWeight: 'bold',
    color: '#222',
  },
});

export default UserProfileScreen;
